import React, { useEffect, useRef, useState } from 'react';
import Icon from '@mdi/react';
import {
    mdiClose,
    mdiCogSyncOutline,
    mdiHistory,
    mdiImagePlusOutline,
    mdiRobotOutline,
    mdiSend,
    mdiRefresh,
} from '@mdi/js';
import Botao from '@/components/Botao';
import AlertBox from '@/components/layout/AlertBox';
import { useAppliance } from '@/lib/useAppliance';
import { useAssistente } from '@/lib/useAssistente';
import { invalidarPorOperacoes } from '@/lib/iaInvalidacao';
import { imagemDoDataTransfer, prepararImagem, TIPOS_ACEITOS, type ImagemPreparada } from '@/lib/imagemChat';
import type { IaOperacaoDTO } from '@webip/shared/src/dto/ia';
import { toastError, toastSuccess } from '@/lib/toast';
import ApiCall from '@/lib/apiCall';
import { useSyncJob } from '@/lib/syncJob';
import DryRunModal, { DryRunAcao } from '@/pages/appliances/DryRunModal';
import PlanoRevisao from './PlanoRevisao';
import Markdown from './Markdown';
import HistoricoConversas from './HistoricoConversas';
import styles from './AssistentePanel.module.css';

/**
 * Painel lateral do assistente de configuração.
 *
 * Depois de aplicar um plano, encadeia o fluxo que já existe na tela de
 * appliances: dry-run (mostra o que mudaria no equipamento) e, se o operador
 * confirmar, o sincronismo pelo agente.
 */

type Props = {
    aberto: boolean;
    onFechar: () => void;
};

const AssistentePanel = ({ aberto, onFechar }: Props) => {
    const { applianceId, selectedAppliance } = useAppliance();
    const { estado, ocupado, enviar, responder, aplicar, descartar, reiniciar, carregarConversa } =
        useAssistente(applianceId);

    const [texto, setTexto] = useState('');
    const [imagem, setImagem] = useState<ImagemPreparada | null>(null);
    const [preparandoImagem, setPreparandoImagem] = useState(false);
    const [arrastando, setArrastando] = useState(false);
    const arquivoRef = useRef<HTMLInputElement | null>(null);
    const [selecionadas, setSelecionadas] = useState<string[]>([]);
    const [aplicando, setAplicando] = useState(false);
    const [mostrarHistorico, setMostrarHistorico] = useState(false);

    const [dryRunAberto, setDryRunAberto] = useState(false);
    const [dryRunLoading, setDryRunLoading] = useState(false);
    const [dryRunAcoes, setDryRunAcoes] = useState<DryRunAcao[]>([]);
    const [dryRunErro, setDryRunErro] = useState<string | null>(null);

    const fimRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [estado.mensagens.length, estado.parcial, estado.operacoes.length]);

    useEffect(() => setSelecionadas([]), [estado.pergunta?.tool_use_id]);

    const submeter = (e: React.FormEvent) => {
        e.preventDefault();
        const valor = texto.trim();
        if ((!valor && !imagem) || preparandoImagem) return;
        setTexto('');
        setImagem(null);
        void enviar(valor, imagem);
    };

    /**
     * Uma imagem por mensagem: colar (Ctrl+V), o botão de anexar e soltar
     * sobre o campo passam todos por aqui. A redução de tamanho acontece no
     * cliente antes de qualquer envio.
     */
    const anexarImagem = async (arquivo: File | null) => {
        if (!arquivo || ocupado) return;
        setPreparandoImagem(true);
        try {
            setImagem(await prepararImagem(arquivo));
        } catch (err) {
            toastError(err instanceof Error ? err.message : 'Não foi possível anexar a imagem');
        } finally {
            setPreparandoImagem(false);
        }
    };

    const aoColar = (e: React.ClipboardEvent) => {
        const arquivo = imagemDoDataTransfer(e.clipboardData);
        if (!arquivo) return; // texto colado segue o fluxo normal do textarea
        e.preventDefault();
        void anexarImagem(arquivo);
    };

    const aoSoltar = (e: React.DragEvent) => {
        e.preventDefault();
        setArrastando(false);
        void anexarImagem(imagemDoDataTransfer(e.dataTransfer));
    };

    // Trocar de conversa ou reiniciar descarta o anexo pendente.
    useEffect(() => setImagem(null), [estado.conversa?.id]);

    const executarDryRun = async (applianceAlvo: number) => {
        setDryRunAberto(true);
        setDryRunLoading(true);
        setDryRunAcoes([]);
        setDryRunErro(null);
        try {
            const res = await ApiCall.post(`/api/appliance/${applianceAlvo}/sync-dryrun`, {});
            if (res?.ok === false) setDryRunErro(res?.erro ?? 'Falha no dry-run');
            setDryRunAcoes(res?.acoes ?? []);
        } catch (err: any) {
            setDryRunErro(err?.response?.data?.msg ?? 'Falha ao simular o sincronismo');
        } finally {
            setDryRunLoading(false);
        }
    };

    const aoAplicar = async (segredos: Record<string, Record<string, string>>) => {
        setAplicando(true);
        try {
            const resultado = await aplicar(segredos);
            if (!resultado?.ok) {
                toastError(resultado?.erro ?? 'Não foi possível aplicar o plano');
                return;
            }
            toastSuccess('Plano aplicado. Confira o que será enviado ao equipamento.');

            // O assistente grava por fora das telas: sem invalidar, quem estiver
            // com a lista de redes/VPNs/appliances aberta segue vendo o estado
            // anterior até recarregar a página.
            invalidarPorOperacoes(
                (resultado.operacoes ?? [])
                    .filter((o: IaOperacaoDTO) => o.status === 'aplicada')
                    .map((o: IaOperacaoDTO) => o.ferramenta),
            );

            const alvo = resultado.appliance_id ?? (applianceId ? Number(applianceId) : null);
            if (alvo) await executarDryRun(alvo);
        } finally {
            setAplicando(false);
        }
    };

    // Sync como long-running job: dispara (o POST volta na hora) e o
    // useSyncJob emite o toast do status final no polling. O modal de dry-run
    // permanece aberto com o botão em "Sincronizando..." (confirmLoading) até
    // o fim — então o fecha.
    const sync = useSyncJob(applianceId ? Number(applianceId) : null);
    const [syncDoDryRun, setSyncDoDryRun] = useState(false);

    const sincronizar = () => {
        setSyncDoDryRun(true);
        void sync.disparar();
    };

    useEffect(() => {
        if (!syncDoDryRun || sync.rodando) return;
        setSyncDoDryRun(false);
        setDryRunAberto(false);
    }, [syncDoDryRun, sync.rodando]);

    const pergunta = estado.pergunta;

    const aoReiniciar = () => {
        setMostrarHistorico(false);
        reiniciar();
    };

    const aoSelecionarConversa = async (id: number) => {
        await carregarConversa(id);
        setMostrarHistorico(false);
    };

    return (
        <>
            <aside className={[styles.painel, aberto ? styles.aberto : ''].join(' ')} aria-hidden={!aberto}>
                <header className={styles.cabecalho}>
                    <Icon path={mdiRobotOutline} size={1} />
                    <div className={styles.tituloBloco}>
                        <strong>Gestão com IA</strong>
                        <span className={styles.subtitulo}>
                            {selectedAppliance?.nome ?? 'Nenhuma appliance selecionada'}
                        </span>
                    </div>
                    <Botao
                        variant="none-secondary"
                        size="sm"
                        onClick={() => setMostrarHistorico((v) => !v)}
                        title="Histórico de conversas"
                    >
                        <Icon path={mdiHistory} size={0.85} />
                    </Botao>
                    <Botao variant="none-secondary" size="sm" onClick={aoReiniciar} title="Nova conversa">
                        <Icon path={mdiRefresh} size={0.85} />
                    </Botao>
                    <Botao variant="none-secondary" size="sm" onClick={onFechar} title="Fechar">
                        <Icon path={mdiClose} size={0.9} />
                    </Botao>
                </header>

                {mostrarHistorico ? (
                    <div className={styles.corpo}>
                        <HistoricoConversas
                            conversaAtualId={estado.conversa?.id}
                            onSelecionar={(id) => void aoSelecionarConversa(id)}
                        />
                    </div>
                ) : (
                <div className={styles.corpo}>
                    {estado.mensagens.length === 0 && !estado.parcial && (
                        <div className={styles.vazio}>
                            <p>Descreva o que você precisa configurar. Por exemplo:</p>
                            <ul>
                                <li>“Cria uma rede wifi de convidados isolada”</li>
                                <li>“Quero uma VPN wireguard para o pessoal acessar de casa”</li>
                                <li>“Publica a porta 3389 do servidor 192.168.1.50, só do Brasil”</li>
                            </ul>
                            <p className={styles.vazioNota}>
                                Nada é alterado sem você revisar e aprovar o plano.
                            </p>
                        </div>
                    )}

                    {estado.mensagens.map((msg, i) => (
                        <div
                            key={i}
                            className={msg.papel === 'usuario' ? styles.msgUsuario : styles.msgAssistente}
                        >
                            {msg.imagens?.length ? (
                                <div className={styles.imagensMsg}>
                                    {msg.imagens.map((src, j) => (
                                        <img key={j} src={src} alt="Imagem enviada" className={styles.imagemMsg} />
                                    ))}
                                </div>
                            ) : null}
                            {/* Só a resposta do assistente vira markdown; o que o
                                operador digitou fica como ele escreveu. */}
                            {msg.papel === 'usuario' ? msg.texto : <Markdown>{msg.texto}</Markdown>}
                            {msg.ferramentas?.length ? (
                                <div className={styles.ferramentas}>
                                    {msg.ferramentas.map((f) => (
                                        <span key={f} className={styles.chipFerramenta}>
                                            {f}
                                        </span>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ))}

                    {estado.parcial && (
                        <div className={styles.msgAssistente}>
                            <Markdown>{estado.parcial}</Markdown>
                        </div>
                    )}

                    {estado.ferramentaAtual ? (
                        <div className={styles.trabalhando}>
                            <Icon path={mdiCogSyncOutline} size={0.7} spin />
                            <span>{estado.ferramentaAtual}</span>
                        </div>
                    ) : (
                        // O modelo raciocina por 30–100 s antes do primeiro texto
                        // (mais com imagem) e esse raciocínio não é transmitido:
                        // sem isto o painel fica parado e parece travado.
                        ocupado && (
                            <div className={styles.trabalhando}>
                                <Icon path={mdiCogSyncOutline} size={0.7} spin />
                                <span>Pensando…</span>
                            </div>
                        )
                    )}

                    {pergunta?.pergunta?.trim() && (
                        <div className={styles.pergunta}>
                            <span className={styles.perguntaRotulo}>Pergunta</span>
                            <span className={styles.perguntaTexto}>{pergunta.pergunta}</span>
                        </div>
                    )}

                    {pergunta?.opcoes?.length ? (
                        <div className={styles.opcoes}>
                            {pergunta.opcoes.map((opcao, i) => {
                                // Pergunta gravada antes da normalização no backend
                                // pode ter opção sem `valor` — responde com o rótulo.
                                const valor = opcao.valor || opcao.rotulo;
                                const ativa = selecionadas.includes(valor);
                                return (
                                    <button
                                        type="button"
                                        key={`${i}-${valor}`}
                                        title={opcao.descricao ?? undefined}
                                        className={[styles.opcao, ativa ? styles.opcaoAtiva : ''].join(' ')}
                                        onClick={() => {
                                            if (!pergunta.multipla) {
                                                void responder([valor]);
                                                return;
                                            }
                                            setSelecionadas((atual) =>
                                                atual.includes(valor)
                                                    ? atual.filter((v) => v !== valor)
                                                    : [...atual, valor],
                                            );
                                        }}
                                    >
                                        {opcao.rotulo}
                                    </button>
                                );
                            })}
                            {pergunta.multipla && selecionadas.length > 0 && (
                                <Botao size="sm" onClick={() => void responder(selecionadas)}>
                                    Confirmar
                                </Botao>
                            )}
                        </div>
                    ) : null}

                    <PlanoRevisao
                        operacoes={estado.operacoes}
                        aplicando={aplicando}
                        // Uma conversa pode gerar vários planos: as operações já
                        // aplicadas ficam no histórico, e os botões dependem só de
                        // haver pendentes (o backend aplica apenas essas).
                        somenteLeitura={false}
                        onAplicar={(segredos) => void aoAplicar(segredos)}
                        onDescartar={() => void descartar()}
                    />

                    {estado.erro && (
                        <AlertBox variant="danger" style={{ margin: 0 }}>
                            {estado.erro}
                        </AlertBox>
                    )}

                    <div ref={fimRef} />
                </div>
                )}

                {!mostrarHistorico && (
                    <>
                        <form
                            className={[styles.entrada, arrastando ? styles.entradaArrastando : ''].join(' ')}
                            onSubmit={submeter}
                            onDragOver={(e) => {
                                // Durante o dragover o browser ainda não expõe os
                                // arquivos, só os tipos — o filtro real é no drop.
                                if (!e.dataTransfer.types.includes('Files')) return;
                                e.preventDefault();
                                setArrastando(true);
                            }}
                            onDragLeave={() => setArrastando(false)}
                            onDrop={aoSoltar}
                        >
                            {imagem && (
                                <div className={styles.anexo}>
                                    <img src={imagem.dataUrl} alt={imagem.nome} className={styles.anexoMiniatura} />
                                    <span className={styles.anexoNome} title={imagem.nome}>
                                        {imagem.nome}
                                    </span>
                                    <button
                                        type="button"
                                        className={styles.anexoRemover}
                                        onClick={() => setImagem(null)}
                                        title="Remover imagem"
                                        aria-label="Remover imagem"
                                    >
                                        <Icon path={mdiClose} size={0.6} />
                                    </button>
                                </div>
                            )}
                            <div className={styles.entradaLinha}>
                                <input
                                    ref={arquivoRef}
                                    type="file"
                                    accept={TIPOS_ACEITOS.join(',')}
                                    hidden
                                    onChange={(e) => {
                                        void anexarImagem(e.target.files?.[0] ?? null);
                                        e.target.value = '';
                                    }}
                                />
                                <Botao
                                    type="button"
                                    variant="none-secondary"
                                    size="sm"
                                    title="Anexar imagem (ou cole com Ctrl+V)"
                                    disabled={ocupado || preparandoImagem}
                                    onClick={() => arquivoRef.current?.click()}
                                >
                                    <Icon path={mdiImagePlusOutline} size={0.85} />
                                </Botao>
                                <textarea
                                    value={texto}
                                    rows={2}
                                    placeholder={
                                        pergunta ? 'Digite sua resposta...' : 'O que você quer configurar?'
                                    }
                                    disabled={ocupado}
                                    onChange={(e) => setTexto(e.target.value)}
                                    onPaste={aoColar}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            submeter(e);
                                        }
                                    }}
                                />
                                <Botao
                                    type="submit"
                                    size="sm"
                                    disabled={ocupado || preparandoImagem || (!texto.trim() && !imagem)}
                                >
                                    <Icon path={mdiSend} size={0.8} />
                                </Botao>
                            </div>
                        </form>
                        <div style={{ textAlign: 'center', fontSize: '0.75rem', fontStyle: 'italic', color: 'var(--tc-color-gray-600)' }}>IA pode cometer erros. Revise antes de aplicar.</div>
                    </>
                )}
            </aside>

            <DryRunModal
                show={dryRunAberto}
                applianceNome={selectedAppliance?.nome}
                loading={dryRunLoading}
                acoes={dryRunAcoes}
                erro={dryRunErro}
                onClose={() => setDryRunAberto(false)}
                onConfirm={() => void sincronizar()}
                confirmLoading={sync.rodando}
            />
        </>
    );
};

export default AssistentePanel;
